<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell :title="$t('views.selectedGoat.famacha.listTitle')"
               :icon="List"
               :hide-action="true">
    <ValueListTable :data="famacha"
                    :columns="columns"
                    :loading="false"
                    @refresh="refreshFamacha"
                    @row-click="openConfirm">
      <template #cell-score="{ item }">
        <div class="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mx-auto border-2 border-gray-300 !text-gray-900"
             :style="getScoreBgStyle((item as FamachaEntry).score)">
          {{ (item as FamachaEntry).score }}
        </div>
      </template>

      <template #toolbar-actions>
        <button @click="showAddDialog = true"
                class="btn h-[42px] px-4 rounded-lg flex items-center gap-2 font-medium text-white shadow-sm transition-transform active:scale-95-white"
                :style="{
                  backgroundColor: 'var(--color-success)',
                  border: '1px solid var(--color-success)'
                }">
          <Plus class="w-5 h-5" />
          <span>{{ $t('common.buttons.add') }}</span>
        </button>
      </template>
    </ValueListTable>
    <Teleport to="body">
      <ConfirmActionModule v-if="showConfirm && selectedFamacha"
                           :message="$t(
                            'views.selectedGoat.famacha.deleteAskMessage',
                            { 
                              date: formatDate(selectedFamacha.measuredAt),
                            }
                          )"
                           :confirmMessage="$t('common.buttons.delete')"
                           variant="danger"
                           :confirmLabel="$t('common.buttons.delete')"
                           @cancel="closeConfirm"
                           @confirm="deleteConfirmed" />
    </Teleport>

  </ModuleShell>

  <!-- Add Famacha Dialog -->
  <ValueInput :show="showAddDialog"
              :title="goat?.eartag || ''"
              :subtitle="goat?.name || ''"
              :inputLabel="$t('views.session.famacha.label')"
              :placeholder="$t('views.session.famacha.placeholder')"
              :inputMode="'famacha'"
              :goatId="goatId"
              :showDateInput="true"
              @save="handleFamachaSaved"
              @cancel="showAddDialog = false" />
</template>

<script setup
        lang="ts">
        import { ref, computed } from 'vue'
        import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
        import ValueInput from '@/components/ui/inputs/ValueInput.vue'
        import { List, Plus } from 'lucide-vue-next'
        import { useFamachaStore } from '@/stores/famacha.store.js'
        import ValueListTable from '@/components/ui/tables/ValueListTable.vue'
        import { useI18n } from 'vue-i18n'
        import { useGoatStore } from '@/stores/goat.store.js'
        import ConfirmActionModule from '@/components/actions/ConfirmAction.vue'
        import { formatDate } from '@/utils/format/dateformatting'

        const { t } = useI18n()

        interface FamachaEntry {
          id: number
          score: number
          measuredAt: string
          goatId: number
        }

        const columns = [
          { key: 'measuredAt', label: t('common.valueTableList.famachaDate'), type: 'date' },
          { key: 'score', label: t('common.valueTableList.famachaValue'), center: true, type: 'number' },
        ]

        const props = defineProps<{
          famacha: FamachaEntry[]
          goatId: number
          refreshFamacha: () => Promise<void> | void
        }>()


        const famachaStore = useFamachaStore()
        const goatStore = useGoatStore()
        const showAddDialog = ref(false)
        const showConfirm = ref(false)
        const selectedFamacha = ref(null)

        const goat = computed(() => {
          return goatStore.goats.find(g => g.id === props.goatId)
        })

        async function handleFamachaSaved({ score, date }) {
          const measuredAt = date
            ? new Date(date + 'T' + new Date().toTimeString().split(' ')[0]).toISOString()
            : new Date().toISOString()

          try {
            await famachaStore.createFamachaCheck({
              goatId: props.goatId,
              score: score,
              measuredAt: measuredAt
            })
            showAddDialog.value = false
            await props.refreshFamacha()
          } catch (error) {
            console.error('Failed to add FAMACHA check:', error)
          }
        }

        function openConfirm(famacha) {
          selectedFamacha.value = famacha
          showConfirm.value = true
        }
        function closeConfirm() {
          showConfirm.value = false
          selectedFamacha.value = null
        }
        async function deleteConfirmed() {
          if (!selectedFamacha.value) return

          try {
            await famachaStore.deleteFamachaCheck(selectedFamacha.value.id)
            await props.refreshFamacha()
            closeConfirm()
          } catch (e) {
            console.error('Failed to delete FAMACHA check:', e)
          }
        }

        function getScoreBgStyle(score) {
          const styles = {
            1: 'background-color: #f94943',
            2: 'background-color: #ff7963',
            3: 'background-color: #faa89e',
            4: 'background-color: #fedcca',
            5: 'background-color: #ffffff'
          }
          return styles[score] || 'background-color: #6b7280'
        }

</script>

<style scoped></style>
